
import React, { useState, useEffect, ErrorInfo } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from 'react-router-dom';
import Index from './pages/Index';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import CheckoutPage from './pages/CheckoutPage';
import ThankYouPage from './pages/ThankYouPage';
import NotFound from './pages/NotFound';
import AdminPage from './pages/AdminPage';
import AdminOrdersPage from './pages/AdminOrdersPage';
import ContactPage from './pages/ContactPage';
import RefundPage from './pages/RefundPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AboutPage from './pages/AboutPage';
import { isCloudflareWorker } from './utils/environment';
import { AdminAuthProvider } from './context/AdminAuthContext';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error: Error | null; errorInfo: ErrorInfo | null }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({ errorInfo });
    
    try {
      if (typeof window !== 'undefined') {
        console.log("Environment:", {
          isCloudflareWorker: isCloudflareWorker(),
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        });
      }
    } catch (e) {
      console.error("Error logging details:", e);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="p-6 max-w-md mx-auto bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h2>
          <p className="mb-4">Please try refreshing the page or navigate to a different section.</p>
          
          <div className="bg-gray-100 p-3 rounded-md mb-4 overflow-auto max-h-[200px]">
            <p className="text-sm font-mono">{this.state.error?.toString()}</p>
            {this.state.error?.stack && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sm text-blue-600">Show stack trace</summary>
                <pre className="mt-2 text-xs overflow-auto whitespace-pre-wrap">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex-1"
            >
              Refresh Page
            </button>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 flex-1"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary>
      {children}
    </ErrorBoundary>
  );
};

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        setLoading(false);
      } catch (error) {
        console.error("Error during loading state transition:", error);
        setLoading(false);
      }
    }, 500);
    
    return () => {
      try {
        clearTimeout(timer);
      } catch (error) {
        console.error("Error clearing timeout:", error);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <AdminAuthProvider>
          <Routes>
            <Route path="/" element={<RouteErrorBoundary><Index /></RouteErrorBoundary>} />
            <Route path="/login" element={<RouteErrorBoundary><LoginPage /></RouteErrorBoundary>} />
            <Route path="/signup" element={<RouteErrorBoundary><SignupPage /></RouteErrorBoundary>} />
            {/* Add register route that redirects to signup to handle email links */}
            <Route path="/register" element={<Navigate to="/signup" replace />} />
            <Route path="/dashboard" element={<RouteErrorBoundary><DashboardPage /></RouteErrorBoundary>} />
            <Route path="/checkout" element={<RouteErrorBoundary><CheckoutPage /></RouteErrorBoundary>} />
            <Route path="/thank-you" element={<RouteErrorBoundary><ThankYouPage /></RouteErrorBoundary>} />
            <Route path="/admin" element={<RouteErrorBoundary><AdminPage /></RouteErrorBoundary>} />
            <Route path="/admin/orders" element={<AdminOrdersPage />} />
            {/* New compliance pages */}
            <Route path="/contact" element={<RouteErrorBoundary><ContactPage /></RouteErrorBoundary>} />
            <Route path="/refund" element={<RouteErrorBoundary><RefundPage /></RouteErrorBoundary>} />
            <Route path="/privacy" element={<RouteErrorBoundary><PrivacyPage /></RouteErrorBoundary>} />
            <Route path="/terms" element={<RouteErrorBoundary><TermsPage /></RouteErrorBoundary>} />
            <Route path="/about" element={<RouteErrorBoundary><AboutPage /></RouteErrorBoundary>} />
            <Route path="*" element={<RouteErrorBoundary><NotFound /></RouteErrorBoundary>} />
          </Routes>
        </AdminAuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
